import { useCallback, useMemo, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'query-string';
import { LastOf, Push } from './types';


type SetterPairTuple<T, L = LastOf<T>, N = [T] extends [never] ? true : false> = true extends N ? [] : Push<SetterPairTuple<Exclude<T, L>>, (
  [string | undefined, (value: any | undefined) => void]
)>;

type SearchParamsSetterTuple<Params extends object> = [
  ...SetterPairTuple<keyof Params>,
  [Record<string, any>, (values: Record<string, any>) => void]
]


function useSearchParams<Params extends object>(params: Params): SearchParamsSetterTuple<Params>{
  const { search, pathname } = useLocation();
  const history = useHistory();
  const parsed = useMemo(() => queryString.parse(search || ''), [search]);

  const historyRef = useRef(history);
  historyRef.current = history;
  const parsedRef = useRef(parsed);
  parsedRef.current = parsed;

  const setQueryString = useCallback((
    pathname: string,
    qsparams: Record<string, any>
  ) => {
    historyRef.current.push(`${pathname}?${queryString.stringify(qsparams)}`);
  }, [historyRef]);
    
  const setParam = useCallback((
    param: string,
    value: any
  ) => {
    parsedRef.current = Object.entries({...(parsedRef.current || {}), [param]: value}).reduce((_, [k, v]) => {
      if (v !== undefined) {
        _[k] = v;
      }

      return _;
    }, {} as Record<string, string>);
    setQueryString(pathname, parsedRef.current);
  }, [pathname]);

  const paramList = useMemo(() => Object.keys(params), [Object.keys(params).join(',')]);

  const setters = useMemo(() => [
    ...paramList.map(param => (
      (value: any) => setParam(param, value)
    )),
    (newparams: Record<string, any>) => {
      parsedRef.current = {...newparams};
      setQueryString(pathname, parsedRef.current);
    }
  ], [pathname, setParam, paramList]);

  return [
    ...paramList.map((param, idx) => [
      parsedRef.current[param] || (params as any)[param],
      setters[idx]
    ]),
    [
      parsedRef.current,
      setters[setters.length - 1]
    ]
  ] as SearchParamsSetterTuple<Params>;
}


export default useSearchParams;